import React, { useEffect, useRef, useState } from 'react'
import {
  motion,
  useScroll,
  useTransform,
} from 'framer-motion'

import Heading from '../../atoms/Heading/Heading'
import whatwebuildbg from '../../../app/assets/images/homepage/whatwebuildbg.webp'

const cards = [
  {
    number: '01',
    title: 'AI Agents & Agentic Systems',
    para:
      'Purpose-built agents that plan, reason, and execute multi-step tasks — lead research, ops orchestration, document processing — with human oversight gates built in. Powered by Claude, GPT-4o, and open-weight models.',
    highlight: 'agentic AI · LLM agents',
    color: '#229CFF',
  },
  {
    number: '02',
    title: 'Workflow & Process Automation',
    para:
      'End-to-end automation of revenue and ops workflows via n8n, Make.com, and custom pipelines. CRM syncs, reporting, document handling, and cross-system integrations that eliminate manual effort at scale.',
    highlight: '· Make.com · workflow automation',
    color: '#5A47F9',
  },
  {
    number: '03',
    title: 'SaaS Product Engineering',
    para:
      'From 0-to-1 SaaS builds to scaling existing platforms. Full-stack delivery with React, Next.js, Node.js, Python, FastAPI, and Supabase — with AI features baked in by default, not bolted on.',
    highlight: 'SaaS development · full-stack engineering',
    color: '#229CFF',
  },
  {
    number: '04',
    title: 'Fintech & Web3 Infrastructure',
    para:
      'Payment flows, wallet integrations, smart contract architecture, DeFi tooling, and compliance-ready fintech backends. Built for founders who understand that financial infrastructure is the product.',
    highlight: 'fintech development · Web3 · smart contracts',
    color: '#5A47F9',
  },
]

// Desktop-only: scroll-linked scale + Y
const cardItemVariants = {
  hidden: { opacity: 0, y: 60, scale: 0.97 },
  show: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.55,
      delay: index * 0.08,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
}

// Mobile: simple fade-up, once only, no scale fight
const mobileCardVariants = {
  hidden: { opacity: 0, y: 32 },
  show: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      delay: index * 0.06,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
}

type CardProps = {
  item: (typeof cards)[number]
  index: number
  isActive: boolean
  setCardRef: (index: number, el: HTMLDivElement | null) => void
  isMobile: boolean
}

const StickyCard: React.FC<CardProps> = ({
  item,
  index,
  isActive,
  setCardRef,
  isMobile,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null)

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'start center'],
  })

  // Only used on desktop — on mobile these stay at neutral values
  const cardScale = useTransform(scrollYProgress, [0, 1], isMobile ? [1, 1] : [0.93, 1])
  const cardY = useTransform(scrollYProgress, [0, 1], isMobile ? [0, 0] : [80, 0])

  return (
    <motion.div
      ref={(el) => {
        cardRef.current = el
        setCardRef(index, el)
      }}
      initial="hidden"
      whileInView="show"
      // once: true on mobile prevents re-trigger on slow upward scroll
      viewport={{ once: isMobile, amount: isMobile ? 0.12 : 0.28 }}
      variants={isMobile ? mobileCardVariants : cardItemVariants}
      custom={index}
      style={{
        scale: cardScale,
        y: cardY,
        top: `calc(1.8rem + ${index * 0.9}rem)`,
        zIndex: index + 1,
        transformOrigin: 'center top',
      }}
      className="sticky mb-[4vh] md:mb-[3vh] lg:mb-[4vh]"
    >
      <div
        style={{
          borderColor: isActive ? item.color : 'transparent',
          boxShadow: isActive
            ? `0px 20px 80px ${item.color}30`
            : '0 10px 34px rgba(0,0,0,0.36)',
        }}
        className="relative overflow-hidden rounded-[28px] border-[10px] bg-[#151515] transition-all duration-300 will-change-transform"
      >
        {/* Background Image */}
        <img
          src={whatwebuildbg}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col gap-5 px-5 py-6 md:px-8 md:py-8 xl:px-12 xl:py-10">
          <div
            className="flex h-[60px] w-[60px] items-center justify-center rounded-[25px] md:h-[75px] md:w-[75px] xl:h-[90px] xl:w-[90px]"
            style={{ background: '#212121' }}
          >
            <h3
              className="font-google-sans-bold text-2xl md:text-3xl xl:text-4xl"
              style={{ color: item.color }}
            >
              {item.number}
            </h3>
          </div>

          <div className="flex flex-col gap-5">
            <h2 className="font-google-sans-bold text-2xl text-white md:text-3xl xl:text-4xl">
              {item.title}
            </h2>
            <p className="max-w-[1100px] font-google-sans-regular text-sm leading-[1.8] text-white/80 md:text-base xl:text-lg 2xl:text-xl">
              {item.para}
            </p>
            <h4
              className="font-google-sans-medium text-lg md:text-xl xl:text-2xl"
              style={{ color: item.color }}
            >
              {item.highlight}
            </h4>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

const WhoWeWorkWithSection: React.FC<{ data?: any }> = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  // Detect mobile once on mount + resize
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const setCardRef = (index: number, el: HTMLDivElement | null) => {
    cardRefs.current[index] = el
  }

  useEffect(() => {
    const updateActiveCard = () => {
      let nextActive = 0
      cardRefs.current.forEach((el, index) => {
        if (!el) return
        const rect = el.getBoundingClientRect()
        const stickyTop = parseFloat(window.getComputedStyle(el).top || '0')
        if (rect.top <= stickyTop + 2) {
          nextActive = index
        }
      })
      setActiveIndex(nextActive)
    }

    updateActiveCard()
    window.addEventListener('scroll', updateActiveCard, { passive: true })
    window.addEventListener('resize', updateActiveCard)
    return () => {
      window.removeEventListener('scroll', updateActiveCard)
      window.removeEventListener('resize', updateActiveCard)
    }
  }, [])

  if (!data) return null

  const sectionCards = data.checklist?.length
    ? data.checklist.map((item: { title?: string; body?: string }, index: number) => ({
        ...cards[index],
        title: item.title || cards[index]?.title,
        para: item.body || cards[index]?.para,
      }))
    : cards

  return (
    <section className="relative overflow-visible bg-[#0F0F0F] bg-common-section py-10 md:py-14 xl:py-20">
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: false }}
        className="relative mx-auto w-full max-w-[1140px] px-5 sm:px-8 md:px-12 lg:px-16"
      >
        <div className="text-center">
          <Heading
            title={data.label}
            subTitle={data.heading}
            text={data.subheading}
            className="!px-0"
          />
        </div>
      </motion.div>

      <div className="relative mx-auto mt-10 flex w-[85%] lg:w-[75%] flex-col md:mt-14">
        {sectionCards.map((item: (typeof cards)[number], index: number) => (
          <StickyCard
            key={item.number}
            item={item}
            index={index}
            isActive={index === activeIndex}
            setCardRef={setCardRef}
            isMobile={isMobile}
          />
        ))}
        <div className="h-[7vh] md:h-[8vh] lg:h-[9vh]" aria-hidden />
      </div>
    </section>
  )
}

export default WhoWeWorkWithSection